import { ProductDetailCarbonRatingCard } from '@app/pcf/product/product-detail/components/product-detail-carbon-rating-card';
import ProductDetailLifecycleStageEmissionsSection from '@app/pcf/product/product-detail/components/product-detail-lifecycle-stage-emissions-section';
import { ProductDetailTotalEmissionCard } from '@app/pcf/product/product-detail/components/product-detail-total-emission-card';
import { roundNumberToNDecimalPlaces } from '@app/utils/helpers';
import { ProductResult } from '@shared/api/types';
import { capitalizeFirstLetter, formatDate } from '@shared/lib/utils';
import { ProductPdfPage } from './product-pdf-page';

interface ProductPdfContentProps {
  productResult: ProductResult;
  organizationName: string;
  /** False when further pages (e.g. the ingredient breakdown) follow. */
  last?: boolean;
}

export function ProductPdfContent({
  productResult,
  organizationName,
  last = true,
}: ProductPdfContentProps) {
  const {
    name,
    assessmentDate,
    systemBoundary,
    totalIngredientWeight,
    servings,
  } = productResult;

  const productWeightDisplay = (() => {
    if (totalIngredientWeight) {
      // totalIngredientWeight is kg (the generated type's comment says grams).
      return `${roundNumberToNDecimalPlaces(totalIngredientWeight, 2)} kg`;
    }
    return '-';
  })();

  const dataPoints = [
    {
      label: 'Assessment date',
      value: assessmentDate
        ? formatDate(assessmentDate, 'do MMM yyyy')
        : 'Pending',
    },
    {
      label: 'System boundary',
      value: systemBoundary ? capitalizeFirstLetter(systemBoundary) : '-',
    },
    { label: 'Product weight', value: productWeightDisplay },
    {
      label: 'Number of servings',
      value: servings
        ? roundNumberToNDecimalPlaces(servings, 0).toString()
        : '-',
    },
  ];

  return (
    <ProductPdfPage last={last}>
      {/* Body */}
      <div className="flex flex-1 flex-col gap-5 px-6 pb-6 pt-10">
        {/* Product name + description */}
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-[11px] font-bold uppercase text-primary-600">
              {organizationName}
            </p>
            <h1 className="text-2xl font-bold text-primary-800">{name}</h1>
          </div>
          <p className="max-w-form text-xs text-gray-500">
            This product was assessed using{' '}
            <a
              href="https://myemissions.co"
              className="font-semibold text-teal-500"
            >
              My Emissions
            </a>
            . This is a summary of the assessment results, which can be viewed
            in full on the My Emissions platform.
          </p>
        </div>

        {/* 4 data points */}
        <div className="grid grid-cols-4 gap-4">
          {dataPoints.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-1">
              <p className="font-heading text-xs font-bold text-primary-600">
                {label}
              </p>
              <p className="text-sm font-light">{value}</p>
            </div>
          ))}
        </div>

        {/* Carbon rating + Total emissions side by side */}
        <div className="grid grid-cols-2 gap-4">
          <ProductDetailCarbonRatingCard
            productResult={productResult}
            showLearnMore={false}
            pdf
          />
          <ProductDetailTotalEmissionCard productResult={productResult} pdf />
        </div>

        {/* Lifecycle stage emissions */}
        <div className="max-w-2xl space-y-5">
          <ProductDetailLifecycleStageEmissionsSection
            product={productResult}
            pdf
          />

          {/* Assessment methodology */}
          <div className="flex flex-col gap-2">
            <p className="font-heading text-md font-bold text-primary-700">
              Assessment methodology
            </p>
            <p className="max-w-form text-xs leading-relaxed text-gray-500">
              The emissions for this product were calculated based on the
              product data entered into the My Emissions platform. Emission
              values are based on standard third-party emission factors in our
              database or primary supplier data where provided.
            </p>
            <p className="max-w-form text-xs leading-relaxed text-gray-500">
              The system boundary used was{' '}
              <strong className="font-bold text-gray-500">
                {systemBoundary ? systemBoundary : 'cradle-to-store'}
              </strong>
              , which includes the emissions from farming, manufacturing,
              packaging and transport. Emissions from retail, consumer storage
              and transport, cooking, and end of life are not included.
            </p>
          </div>
        </div>
      </div>
    </ProductPdfPage>
  );
}
